// src/components/Dashboard/SLAUserDashboard.tsx
import React, { useState, useEffect } from "react";
import { Select, Spin, Alert, DatePicker, Radio, Button } from "antd";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import {
  SyncOutlined,
  DownloadOutlined,
  ArrowLeftOutlined,
  UserOutlined,
  MailOutlined,
} from "@ant-design/icons";

// Import SLA-specific components
import SLAUserSummaryCards from "./cards/SLAUserSummaryCards";
import SLARequirementCompliance from "./SLARequirementCompliance";
import api from "../../api";

const { RangePicker } = DatePicker;
const { Option } = Select;

interface User {
  user_id: string;
  user_name: string;
  user_email: string;
  team?: string;
}

const SLAUserDashboard: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Filter and state variables - FIXED: Changed default period
  const [period, setPeriod] = useState<string>("last_30_days");
  const [dateRange, setDateRange] = useState<[string, string] | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [userData, setUserData] = useState<any>(null);
  const [userInfo, setUserInfo] = useState<User | null>(null);
  const [filterType, setFilterType] = useState<"preset" | "custom">("preset");

  const fetchData = async () => {
    if (!userId) return;

    setLoading(true);
    setError(null);

    try {
      console.log("Starting SLA data fetch...");

      // Prepare query parameters
      let params: Record<string, string> = {};

      if (filterType === "preset") {
        // Map frontend period values to backend expected values
        const periodMapping: Record<string, string> = {
          week: "last_7_days",
          month: "last_30_days",
          quarter: "last_90_days",
        };

        params.period = periodMapping[period] || period;
        console.log(`Period mapping: ${period} → ${params.period}`);
      } else if (filterType === "custom" && dateRange) {
        params.start_date = dateRange[0];
        params.end_date = dateRange[1];
      } else if (filterType === "custom" && !dateRange) {
        // FIXED: Don't fetch if custom mode but no dates selected
        console.log(
          "Custom mode selected but no date range provided - skipping fetch",
        );
        setLoading(false);
        return;
      } else {
        // REMOVED: No more hard-coded fallback dates
        console.log("No valid filter parameters - skipping fetch");
        setLoading(false);
        return;
      }

      // Add level parameter from URL if present
      const levelParam = searchParams.get("level");
      if (levelParam) {
        params.level = levelParam;
      }

      console.log("API Parameters:", params);

      // Construct query string
      const queryString = new URLSearchParams(params).toString();

      // 🎯 CORRECT: Use the user-specific endpoint
      const userResponse = await api.get(
        `/metrics/sla/user/${userId}/?${queryString}`,
      );

      console.log("User SLA data received:", userResponse.data);

      // 🔧 Transform the response to match expected format
      const apiData = userResponse.data;

      // Create the expected data structure for components
      const transformedUserData = {
        before_cutoff_compliance: [
          {
            user_id: apiData.user_id,
            user_name: apiData.user_name,
            user_email: apiData.user_email,
            compliance_pct: apiData.before_cutoff_compliance_pct || 0,
            total_processed:
              apiData.compliant_before_cutoff +
              (apiData.total_requirements_processed - apiData.total_compliant),
            compliant: apiData.compliant_before_cutoff || 0,
          },
        ],
        after_cutoff_compliance: [
          {
            user_id: apiData.user_id,
            user_name: apiData.user_name,
            user_email: apiData.user_email,
            compliance_pct: apiData.after_cutoff_compliance_pct || 0,
            total_processed:
              apiData.compliant_after_cutoff +
              (apiData.total_requirements_processed - apiData.total_compliant),
            compliant: apiData.compliant_after_cutoff || 0,
          },
        ],
        combined_compliance: [
          {
            user_id: apiData.user_id,
            user_name: apiData.user_name,
            user_email: apiData.user_email,
            compliance_pct: apiData.overall_compliance_pct || 0,
            before_cutoff_pct: apiData.before_cutoff_compliance_pct || 0,
            after_cutoff_pct: apiData.after_cutoff_compliance_pct || 0,
            total_processed: apiData.total_requirements_processed || 0,
            compliant: apiData.total_compliant || 0,
            avg_time_hours: apiData.avg_verification_time_business_hours || 0,
          },
        ],
      };

      setUserData(transformedUserData);

      // Set user info from API response
      setUserInfo({
        user_id: apiData.user_id,
        user_name: apiData.user_name,
        user_email: apiData.user_email,
        team: apiData.user_groups?.[0] || "unknown", // Get first group as team
      });

      setError(null);
    } catch (err: any) {
      console.error("Error fetching user SLA data:", err);

      // Handle different error scenarios
      if (err.response?.status === 403) {
        setError("No tienes permisos para ver estos datos.");
      } else if (err.response?.status === 404) {
        setError("Usuario no encontrado.");
      } else {
        setError(
          `Error al cargar métricas SLA: ${err?.response?.data?.error || err?.message || "Error desconocido"}`,
        );
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Fetch data on filter changes
  useEffect(() => {
    if (userId) {
      if (filterType === "preset" || (filterType === "custom" && dateRange)) {
        fetchData();
      }
    }
  }, [userId, period, dateRange, filterType]);

  // Event handlers
  const handlePeriodChange = (value: string) => {
    setPeriod(value);
  };

  const handleDateRangeChange = (
    _dates: any,
    dateStrings: [string, string],
  ) => {
    setDateRange(dateStrings);
  };

  const handleFilterTypeChange = (e: any) => {
    const newFilterType = e.target.value;
    setFilterType(newFilterType);

    if (newFilterType === "preset") {
      setDateRange(null);
      // Will automatically use current period
    } else {
      // FIXED: Don't set default dates - wait for user to select
      setDateRange(null); // Clear any existing range
      // Don't fetch data until user selects dates
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  const handleBack = () => {
    navigate("/sla-metrics");
  };

  const exportToCsv = () => {
    if (!userData || !userInfo) return;

    // Get the user data from the transformed structure
    const user = userData.combined_compliance?.[0];
    if (!user) return;

    // Prepare user-specific data for export
    const csvContent = [
      ["Metric", "Value"],
      ["User Name", userInfo.user_name],
      ["User Email", userInfo.user_email],
      ["Team", userInfo.team || ""],
      ["Overall Compliance", `${user.compliance_pct}%`],
      ["Before 1PM Compliance", `${user.before_cutoff_pct}%`],
      ["After 1PM Compliance", `${user.after_cutoff_pct}%`],
      ["Total Processed", user.total_processed],
      ["Total Compliant", user.compliant],
      ["Avg Processing Time", `${user.avg_time_hours} hours`],
    ]
      .map((row) => row.join(","))
      .join("\n");

    const filename = `sla-metrics-${userInfo.user_name.replace(/\s+/g, "-").toLowerCase()}`;

    // Create download link
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `${filename}-${format(new Date(), "yyyy-MM-dd")}.csv`,
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (!userId) {
    return (
      <div className="flex justify-center items-center py-24">
        <Alert
          message="Error"
          description="ID de usuario no proporcionado"
          type="error"
          showIcon
        />
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header with Back Button */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
          <div>
            <div className="flex items-center mb-2">
              <Button
                icon={<ArrowLeftOutlined />}
                onClick={handleBack}
                className="mr-4"
                type="default"
              >
                Volver
              </Button>
              <h1 className="text-2xl font-bold text-gray-900">
                SLA Metrics - Usuario Individual
              </h1>
            </div>
            {userInfo && (
              <div className="flex items-center text-gray-600 ml-12">
                <UserOutlined className="mr-2" />
                <span className="font-medium mr-4">{userInfo.user_name}</span>
                <MailOutlined className="mr-2" />
                <span className="mr-4">{userInfo.user_email}</span>
                {userInfo.team && (
                  <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium capitalize">
                    Equipo {userInfo.team}
                  </span>
                )}
              </div>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {/* Filter Controls */}
            <div className="flex flex-wrap items-center gap-2 mr-4">
              <Radio.Group
                onChange={handleFilterTypeChange}
                value={filterType}
                className="mr-2"
              >
                <Radio.Button value="preset">Predefinido</Radio.Button>
                <Radio.Button value="custom">Custom</Radio.Button>
              </Radio.Group>

              {filterType === "preset" ? (
                <Select
                  defaultValue="last_30_days" // FIXED: Changed from "month"
                  className="w-32"
                  onChange={handlePeriodChange}
                  value={period}
                >
                  <Option value="last_7_days">Últimos 7 Días</Option>{" "}
                  {/* FIXED: Changed from "week" */}
                  <Option value="last_30_days">Últimos 30 Días</Option>{" "}
                  {/* FIXED: Changed from "month" */}
                  <Option value="last_90_days">Últimos 90 Días</Option>{" "}
                  {/* FIXED: Changed from "quarter" */}
                </Select>
              ) : (
                <RangePicker
                  onChange={handleDateRangeChange}
                  className="w-auto"
                />
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2">
              <Button
                icon={<SyncOutlined spin={refreshing} />}
                onClick={handleRefresh}
                disabled={loading || refreshing}
                type="default"
              >
                Refresh
              </Button>
              <Button
                icon={<DownloadOutlined />}
                onClick={exportToCsv}
                disabled={loading || !userData}
                type="default"
              >
                Exportar
              </Button>
            </div>
          </div>
        </div>

        {error && (
          <Alert
            message="Error"
            description={error}
            type="error"
            showIcon
            className="mb-6"
          />
        )}

        {loading ? (
          <div className="flex justify-center items-center py-24">
            <Spin size="large" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* User Summary Cards */}
            <SLAUserSummaryCards userData={userData} userInfo={userInfo} />

            {/* User Performance Chart */}
            {/*<div className="bg-white rounded-lg shadow p-4">*/}
            {/*  <h2 className="text-lg font-medium mb-4 text-gray-700">*/}
            {/*    Tendencia de Cumplimiento SLA*/}
            {/*  </h2>*/}
            {/*  <SLAUserPerformanceChart userData={userData} />*/}
            {/*</div>*/}

            {/* Requirement Compliance - Always shows user-filtered data */}
            {/*<SLARequirementCompliance*/}
            {/*  dateRange={dateRange || ["2025-04-01", "2025-04-30"]}*/}
            {/*  selectedUser={userId}*/}
            {/*/>*/}
            <SLARequirementCompliance
              dateRange={dateRange}
              selectedUser={userId}
              period={period} // NEW: Pass period
              filterType={filterType} // NEW: Pass filterType
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default SLAUserDashboard;
